import {Box, Button, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowRight01Icon, Calendar03Icon, Clock01Icon, Route01Icon} from "@hugeicons/core-free-icons";
import {c, mono, primaryButtonSx, purpleGradient, softShadow} from "../landingTokens";
import {Section, Eyebrow, SectionHeading, Dim} from "./Shell";

const FEATURES = [
  {
    icon: Calendar03Icon,
    title: "Schedule your outreach",
    body: "Choose a start and end date, then set the daily calling window. Your campaign calls only during those hours.",
  },
  {
    icon: Route01Icon,
    title: "Put your voice agent to work",
    body: "Add your phone numbers, choose a caller ID and connect answered calls to your voice bot for the conversation.",
  },
  {
    icon: Clock01Icon,
    title: "Follow up automatically",
    body: "Set a maximum number of attempts. Unanswered numbers are retried on following days, with call attempts available to review.",
  },
];

const SCHEDULE = [
  {label: "Campaign dates", value: "12–16 October"},
  {label: "Daily calling window", value: "09:00–17:00"},
  {label: "Maximum attempts", value: "3 per number"},
  {label: "Answered calls", value: "Customer outreach agent"},
];

export const Campaigns = () => (
  <Section id="campaigns" label="Campaigns">
    <Box sx={{
      background: "linear-gradient(180deg, #FFFFFF 0%, #F4F1FF 100%)",
      borderRadius: "34px",
      boxShadow: softShadow,
      p: {xs: 3, sm: 4, md: 6},
    }}>
      <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1.1fr 1fr"}, gap: {xs: 4, md: 6}, alignItems: "center"}}>
        <Box className="reveal" sx={{minWidth: 0}}>
          <Eyebrow>Outbound campaigns</Eyebrow>
          <SectionHeading sx={{fontSize: "clamp(32px, 4.2vw, 50px)", mb: 2.5}}>
            Plan the calls.<br />
            <Dim>Let your agent reach out.</Dim>
          </SectionHeading>
          <Typography sx={{fontSize: 17, lineHeight: 1.6, color: c.inkSoft, mb: 3}}>
            Turn a list of phone numbers into a scheduled calling campaign. From product
            promotions to customer follow-ups, OpenBrain handles the dialing and connects
            answered calls to your voice agent.
          </Typography>
          <Button
            href="#demo"
            variant="contained"
            endIcon={<HugeiconsIcon icon={ArrowRight01Icon} size={18} />}
            sx={{
              ...primaryButtonSx,
              minHeight: {xs: 52, md: 56},
              px: 3.5,
              py: 1.75,
              fontSize: 15,
              "&&, &&:hover": {color: c.white},
            }}
          >
            See campaigns in action
          </Button>
        </Box>

        <Box component="figure" className="reveal" sx={{m: 0, minWidth: 0}}>
          <Box sx={{bgcolor: c.white, borderRadius: "22px", boxShadow: softShadow, overflow: "hidden"}}>
            <Stack direction="row" alignItems="center" gap={1.5} sx={{p: {xs: 2.5, sm: 3}, background: purpleGradient, color: c.white}}>
              <HugeiconsIcon icon={Calendar03Icon} size={26} aria-hidden="true" style={{flexShrink: 0}} />
              <Box>
                <Typography sx={{fontSize: 12, letterSpacing: "0.08em", fontFamily: mono, mb: 0.5}}>CAMPAIGN EXAMPLE</Typography>
                <Typography component="h3" sx={{fontSize: 20, fontWeight: 600, letterSpacing: "-0.02em"}}>
                  Community & Engagement
                </Typography>
              </Box>
            </Stack>
            <Box component="dl" sx={{m: 0, px: {xs: 2.5, sm: 3}}}>
              {SCHEDULE.map((item) => (
                <Box key={item.label} sx={{py: 2, borderBottom: `1px solid ${c.line}`, "&:last-child": {borderBottom: 0}}}>
                  <Typography component="dt" sx={{fontSize: 14, color: c.inkSoft, mb: 0.5}}>{item.label}</Typography>
                  <Typography component="dd" sx={{m: 0, fontSize: 16, fontWeight: 600, color: c.ink}}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{mx: {xs: 2.5, sm: 3}, mb: 3, p: 2, borderRadius: "14px", bgcolor: c.bgTint}}>
              <Typography sx={{fontSize: 14, lineHeight: 1.5, color: c.purpleInk}}>
                No answer? Try again on the next day, within your calling window and attempt limit.
              </Typography>
            </Box>
          </Box>
          <Typography component="figcaption" sx={{fontSize: 12, color: c.inkSoft, mt: 1.5, textAlign: "center"}}>
            Your audience, your schedule, your conversation flow.
          </Typography>
        </Box>
      </Box>

      <Box className="reveal-stagger" sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(3, minmax(0, 1fr))"}, gap: 3, mt: {xs: 4, md: 6}, pt: 4, borderTop: `1px solid ${c.line}`}}>
        {FEATURES.map((feature) => (
          <Box key={feature.title}>
            <HugeiconsIcon icon={feature.icon} size={24} color={c.purpleDeep} aria-hidden="true" />
            <Typography component="h3" sx={{fontSize: 19, fontWeight: 600, letterSpacing: "-0.02em", mt: 1.5, mb: 1}}>
              {feature.title}
            </Typography>
            <Typography sx={{fontSize: 16, lineHeight: 1.6, color: c.inkSoft}}>{feature.body}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Section>
);
